/**
 * DataTableFooter Component
 *
 * Composable footer for DataTable with pagination.
 * Renders a full-width border separator and the pagination controls.
 * Registers pagination keyboard shortcuts (←→ pages, Option+[/] per-page)
 * with DataTableShortcuts when nested inside one.
 *
 * Usage:
 * ```tsx
 * <DataTableShortcuts>
 *   <DataTableContent />
 *   <DataTableFooter
 *     currentPage={page}
 *     totalPages={pages}
 *     totalCount={count}
 *     onPageChange={setPage}
 *   />
 * </DataTableShortcuts>
 * ```
 */

import { useEffect } from 'react';
import { cn } from '@/lib/utils';
import { DataTablePagination } from './DataTablePagination';
import { useDataTableShortcuts } from './DataTableShortcuts';
import { DEFAULT_PAGE_SIZE, DEFAULT_PER_PAGE_OPTIONS } from './constants';

export interface DataTableFooterProps {
  /** Current page (1-indexed) */
  readonly currentPage: number;
  /** Total number of pages */
  readonly totalPages: number;
  /** Total count of all items */
  readonly totalCount: number;
  /** Items per page */
  readonly perPage?: number;
  /** Page change handler */
  readonly onPageChange: (page: number) => void;
  /** Per page change handler */
  readonly onPerPageChange?: (perPage: number) => void;
  /** Available per page options */
  readonly perPageOptions?: readonly number[];
  /** Additional className */
  readonly className?: string;
}

export function DataTableFooter({
  currentPage,
  totalPages,
  totalCount,
  perPage = DEFAULT_PAGE_SIZE,
  onPageChange,
  onPerPageChange,
  perPageOptions = DEFAULT_PER_PAGE_OPTIONS,
  className,
}: DataTableFooterProps) {
  const shortcuts = useDataTableShortcuts();

  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  // Register pagination shortcuts with DataTableShortcuts (when available)
  useEffect(() => {
    if (!shortcuts) return;

    return shortcuts.registerPagination({
      canGoPrevious,
      canGoNext,
      onPreviousPage: () => onPageChange(currentPage - 1),
      onNextPage: () => onPageChange(currentPage + 1),
      onCyclePerPage: onPerPageChange
        ? (direction) => {
            const currentIndex = perPageOptions.indexOf(perPage);
            let nextIndex: number;
            if (direction === 'prev') {
              nextIndex = currentIndex <= 0 ? perPageOptions.length - 1 : currentIndex - 1;
            } else {
              nextIndex = currentIndex >= perPageOptions.length - 1 ? 0 : currentIndex + 1;
            }
            onPerPageChange(perPageOptions[nextIndex]);
            onPageChange(1);
          }
        : undefined,
    });
  }, [shortcuts, canGoPrevious, canGoNext, currentPage, onPageChange, onPerPageChange, perPage, perPageOptions]);

  return (
    <div className={cn('flex-shrink-0', className)}>
      <DataTablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        perPage={perPage}
        perPageOptions={perPageOptions}
        canGoPrevious={canGoPrevious}
        canGoNext={canGoNext}
        onPageChange={onPageChange}
        onPerPageChange={onPerPageChange}
      />
    </div>
  );
}
